import react, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  Grid,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Stack,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartIcon from "@mui/icons-material/TableChart";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";

// ---------------------------------------------------------------------------
// AddTicketModal
//
// Self-contained create/edit form for a "Ticket". Mirrors the pattern used
// elsewhere in this app (e.g. AddRoutePlan): a controlled Dialog that takes
// `onClose` and `fetchData`/`onSuccess` callbacks from the parent.
//
// Props:
//   open        bool                       - controls dialog visibility
//   onClose     fn()                       - called when dialog should close
//   onSuccess   fn()                       - called after a successful save (parent should refetch)
//   editMode    bool                       - true when editing an existing ticket
//   editData    object                     - existing ticket data when editMode is true
//   assignees   array<{id, name}>          - optional list for "Assigned To" dropdown
//
// Backend wiring (TODO — update to match your real API):
//   Create:  window.Platform.database.addTicket(formData)
//   Update:  window.Platform.database.updateTicket(formData)
//   Both are called with a FormData payload (multipart) since files are
//   attached. If your backend expects JSON + a separate upload endpoint,
//   split the two calls where marked below.
// ---------------------------------------------------------------------------

const MAX_FILES = 8;
const MAX_FILE_SIZE_MB = 50;

// Must stay in sync with the backend's ALLOWED_MIME_TYPES / ALLOWED_EXTENSIONS
// in the multer config (ticketRoutes.js). Checking mimetype OR extension
// because some browsers/OSes report generic mimetypes for Excel/video files.
const ACCEPTED_MIME_PREFIXES = ["image/", "video/"];
const ACCEPTED_MIME_EXACT = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "application/vnd.ms-excel", // .xls
];
const ACCEPTED_EXTENSIONS = [
  ".jpg", ".jpeg", ".png", ".webp", ".gif",
  ".mp4", ".mov", ".avi", ".mkv", ".webm",
  ".pdf",
  ".xlsx", ".xls",
];
const FILE_INPUT_ACCEPT = "image/*,video/*,application/pdf,.xlsx,.xls";

const getFileKind = (file) => {
  const name = file.name || "";
  const ext = name.slice(name.lastIndexOf(".")).toLowerCase();
  if (file.type?.startsWith("image/")) return "image";
  if (file.type?.startsWith("video/")) return "video";
  if (file.type === "application/pdf" || ext === ".pdf") return "pdf";
  if (
    file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    file.type === "application/vnd.ms-excel" ||
    ext === ".xlsx" || ext === ".xls"
  ) return "excel";
  // Fallback to extension-based guess for images/videos with odd mimetypes
  if ([".jpg", ".jpeg", ".png", ".webp", ".gif"].includes(ext)) return "image";
  if ([".mp4", ".mov", ".avi", ".mkv", ".webm"].includes(ext)) return "video";
  return "file";
};

const isFileTypeAccepted = (file) => {
  const name = file.name || "";
  const ext = name.slice(name.lastIndexOf(".")).toLowerCase();
  const mimeOk =
    ACCEPTED_MIME_PREFIXES.some((prefix) => file.type?.startsWith(prefix)) ||
    ACCEPTED_MIME_EXACT.includes(file.type);
  const extOk = ACCEPTED_EXTENSIONS.includes(ext);
  return mimeOk || extOk;
};

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low", color: "#2563eb" },
  { value: "medium", label: "Medium", color: "#d97706" },
  { value: "high", label: "High", color: "#ea580c" },
  { value: "critical", label: "Critical", color: "#dc2626" },
];

const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "on_hold", label: "On Hold" },
//   { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

const CATEGORY_OPTIONS = [
  "Technical",
  "Sales",
  "Support",
  "Billing",
  "Logistics",
  "Other",
];

const emptyForm = {
  ticketName: "",
  category: "",
  priority: "medium",
  status: "open",
  assignedTo: "",
  dueDate: null,
  description: "",
};

export default function AddTicketModal({
  open = true,
  onClose,
  onSuccess,
  editMode = false,
  editData = null,
  assignees = [],
}) {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [files, setFiles] = useState([]); // { file, previewUrl, type }
  const [existingAttachments, setExistingAttachments] = useState([]); // already-saved files when editing
  const [dragActive, setDragActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (editMode && editData) {
      setForm({
        ticketName: editData.ticketName || editData.name || "",
        category: editData.category || "",
        priority: editData.priority || "medium",
        status: editData.status || "open",
        assignedTo: editData.assignedTo || editData.ownerId || "",
        dueDate: editData.dueDate || null,
        description: editData.description || "",
      });
      setExistingAttachments(editData.attachments || []);
    } else {
      setForm(emptyForm);
      setExistingAttachments([]);
    }
    setFiles([]);
    setErrors({});
  }, [editMode, editData, open]);

  // Revoke object URLs on unmount to avoid memory leaks
  useEffect(() => {
    return () => {
      files.forEach((f) => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
    };
  }, [files]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateAndAddFiles = (fileList) => {
    const incoming = Array.from(fileList);
    const validFiles = [];
    let fileError = "";

    if (files.length + existingAttachments.length + incoming.length > MAX_FILES) {
      fileError = `You can attach up to ${MAX_FILES} files total.`;
    }

    incoming.forEach((file) => {
      const isAccepted = isFileTypeAccepted(file);
      if (!isAccepted) {
        fileError = "Only images, videos, PDFs, and Excel files are allowed.";
        return;
      }
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        fileError = `"${file.name}" exceeds the ${MAX_FILE_SIZE_MB}MB limit.`;
        return;
      }
      const kind = getFileKind(file);
      validFiles.push({
        file,
        previewUrl: (kind === "image" || kind === "video") ? URL.createObjectURL(file) : null,
        type: kind,
        id: `${file.name}-${file.lastModified}-${file.size}`,
      });
    });

    if (fileError) {
      setErrors((prev) => ({ ...prev, files: fileError }));
    } else {
      setErrors((prev) => ({ ...prev, files: undefined }));
    }

    if (validFiles.length) {
      setFiles((prev) => {
        const merged = [...prev, ...validFiles];
        return merged.slice(0, MAX_FILES - existingAttachments.length);
      });
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files?.length) {
      validateAndAddFiles(e.target.files);
    }
    e.target.value = ""; // allow re-selecting the same file
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.length) {
      validateAndAddFiles(e.dataTransfer.files);
    }
  };

  const removeNewFile = (id) => {
    setFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((f) => f.id !== id);
    });
  };

  const removeExistingAttachment = (attachmentId) => {
    setExistingAttachments((prev) => prev.filter((a) => (a.id ?? a.url) !== attachmentId));
  };

  const formatBytes = (bytes) => {
    if (!bytes) return "0 KB";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(0)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const validate = () => {
    const next = {};
    if (!form.ticketName.trim()) next.ticketName = "Ticket name is required.";
    if (!form.description.trim()) next.description = "Description is required.";
    else if (form.description.trim().length < 10) next.description = "Add a bit more detail (min 10 characters).";
    setErrors((prev) => ({ ...prev, ...next }));
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    console.log(form)
    console.log(files)
    if (!validate()) {
      window.NotificationUtils?.showError?.("Please fix the highlighted fields.");
      return;
    }
    setSaving(true);
    setUploadProgress(0);
    try {
      const payload = new FormData();
      payload.append("ticketName", form.ticketName.trim());
      payload.append("category", form.category);
      payload.append("priority", form.priority);
      payload.append("status", form.status);
      payload.append("assignedTo", form.assignedTo);
      payload.append("dueDate", form.dueDate || "");
      payload.append("description", form.description.trim());
      if (editMode && editData?.id) {
        payload.append("id", editData.id);
      }
      // Keep track of which existing attachments the user kept (for edit mode)
    //   existingAttachments.forEach((a) => payload.append("keepAttachmentIds[]", a.id ?? a.url));
      files.map((f) =>{
        return  payload.append("attachments", f.file.name)
        } 
        )
      const apiCall = editMode
        ? window.Platform.database.updateTicket
        : window.Platform.database.createNewTicket;

      if (typeof apiCall !== "function") {
        console.warn("window.Platform.database.addTicket/updateTicket is not implemented yet.");
        window.NotificationUtils?.showError?.("Save endpoint isn't wired up yet — check console for the payload.");
        console.log("Ticket payload (FormData entries):", Array.from(payload.entries()));
        setSaving(false);
        return;
      }

      const results = await apiCall(payload, {
        onUploadProgress: (evt) => {
          if (evt?.total) {
            setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        },
      });

      if (results?.status) {
        window.NotificationUtils?.showError?.(results?.message || "Something went wrong.");
        setSaving(false);
        return;
      }

      window.NotificationUtils?.showSuccess?.(editMode ? "Ticket updated successfully" : "Ticket created successfully");
      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.error(error);
      window.NotificationUtils?.showError?.("Something Went Wrong!");
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  const totalAttachmentCount = files.length + existingAttachments.length;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {editMode ? "Update Ticket" : "Add New Ticket"}
        <IconButton onClick={onClose} disabled={saving} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: "#fafbfc" }}>
        <Grid container spacing={2.5} sx={{ mt: 0.5 }}>

          {/* Ticket name */}
          <Grid item xs={12} sm={7}>
            <TextField
              fullWidth
              label="Ticket Name"
              placeholder="e.g. Delivery delay at Jaipur warehouse"
              value={form.ticketName}
              onChange={handleChange("ticketName")}
              error={!!errors.ticketName}
              helperText={errors.ticketName}
              size="small"
            />
          </Grid>

          {/* Category */}
          {/* <Grid item xs={12} sm={5}>
            <FormControl fullWidth size="small" error={!!errors.category}>
              <InputLabel>Category</InputLabel>
              <Select value={form.category} label="Category" onChange={handleChange("category")}>
                {CATEGORY_OPTIONS.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
              {errors.category && <FormHelperText>{errors.category}</FormHelperText>}
            </FormControl>
          </Grid> */}

          {/* Priority */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select value={form.priority} label="Priority" onChange={handleChange("priority")}>
                {PRIORITY_OPTIONS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    <Chip
                      label={p.label}
                      size="small"
                      sx={{ backgroundColor: `${p.color}1A`, color: p.color, fontWeight: 600 }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Status (mainly relevant in edit mode, but useful on create too) */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={form.status} label="Status" onChange={handleChange("status")}>
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Assigned to */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Assigned To</InputLabel>
              <Select value={form.assignedTo} label="Assigned To" onChange={handleChange("assignedTo")}>
                <MenuItem value="">
                  <em>Select Assignee</em>
                </MenuItem>
                  <MenuItem key={1} value={"developer"}>developer</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Due date */}
          {/* <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Due Date"
              InputLabelProps={{ shrink: true }}
              value={form.dueDate || ""}
              onChange={handleChange("dueDate")}
            />
          </Grid> */}

          {/* Long description */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={6}
              maxRows={12}
              label="Description"
              placeholder="Describe the issue or request in detail — steps to reproduce, impact, relevant context, etc."
              value={form.description}
              onChange={handleChange("description")}
              error={!!errors.description}
              helperText={errors.description || `${form.description.length} characters`}
            />
          </Grid>

          {/* Attachments */}
          <Grid item xs={12}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#374151", mb: 1 }}>
              Attachments (images, videos, PDFs, Excel) — up to {MAX_FILES} files, {MAX_FILE_SIZE_MB}MB each
            </Typography>

            <Box
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: `2px dashed ${dragActive ? "#2563eb" : "#cbd5e1"}`,
                backgroundColor: dragActive ? "#eff6ff" : "#fff",
                borderRadius: "12px",
                p: 3,
                textAlign: "center",
                cursor: totalAttachmentCount >= MAX_FILES ? "not-allowed" : "pointer",
                opacity: totalAttachmentCount >= MAX_FILES ? 0.5 : 1,
                transition: "all 0.15s ease",
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 32, color: "#2563eb", mb: 1 }} />
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>
                Drag & drop files here, or click to browse
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.5 }}>
                {totalAttachmentCount} / {MAX_FILES} attached
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept={FILE_INPUT_ACCEPT}
                multiple
                hidden
                disabled={totalAttachmentCount >= MAX_FILES}
                onChange={handleFileInputChange}
              />
            </Box>
            {errors.files && (
              <Typography sx={{ fontSize: 12, color: "#dc2626", mt: 0.5 }}>{errors.files}</Typography>
            )}

            {(existingAttachments.length > 0 || files.length > 0) && (
              <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                {existingAttachments.map((att) => (
                  <Grid item xs={6} sm={4} md={3} key={att.id ?? att.url}>
                    <AttachmentThumb
                      url={att.url}
                      name={att.name || "Attachment"}
                      type={att.type || getFileKind({ name: att.name || att.url || "", type: "" })}
                      onRemove={() => removeExistingAttachment(att.id ?? att.url)}
                    />
                  </Grid>
                ))}
                {files.map((f) => (
                  <Grid item xs={6} sm={4} md={3} key={f.id}>
                    <AttachmentThumb
                      url={f.previewUrl}
                      name={f.file.name}
                      type={f.type}
                      sizeLabel={formatBytes(f.file.size)}
                      onRemove={() => removeNewFile(f.id)}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>

        {saving && uploadProgress > 0 && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={uploadProgress} sx={{ borderRadius: 5, height: 6 }} />
            <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.5 }}>Uploading… {uploadProgress}%</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px", px: 3 }}
        >
          {saving ? "Saving…" : editMode ? "Update Ticket" : "Create Ticket"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// AttachmentThumb — small preview tile used for both existing and newly
// selected files.
// ---------------------------------------------------------------------------
function AttachmentThumb({ url, name, type, sizeLabel, onRemove }) {
  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: "10px",
        overflow: "hidden",
        border: "1px solid #e3e8ef",
        backgroundColor: "#f3f5f9",
        height: 110,
      }}
    >
      {type === "video" ? (
        <Box sx={{ position: "relative", height: "100%" }}>
          <video src={url} muted style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          <PlayCircleOutlineIcon
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              color: "#fff",
              fontSize: 32,
              filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.6))",
            }}
          />
        </Box>
      ) : type === "image" ? (
        <img src={url} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            color: type === "pdf" ? "#dc2626" : type === "excel" ? "#16a34a" : "#6b7280",
          }}
        >
          {type === "pdf" ? (
            <PictureAsPdfIcon sx={{ fontSize: 36 }} />
          ) : type === "excel" ? (
            <TableChartIcon sx={{ fontSize: 36 }} />
          ) : (
            <InsertDriveFileIcon sx={{ fontSize: 36 }} />
          )}
        </Box>
      )}

      <Tooltip title="Remove">
        <IconButton
          size="small"
          onClick={onRemove}
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            backgroundColor: "rgba(17,24,39,0.65)",
            color: "#fff",
            "&:hover": { backgroundColor: "rgba(17,24,39,0.85)" },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "rgba(17,24,39,0.65)",
          color: "#fff",
          fontSize: 10,
          px: 0.75,
          py: 0.25,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <InsertDriveFileIcon sx={{ fontSize: 12 }} />
        <Typography noWrap sx={{ fontSize: 10, flex: 1 }}>{name}</Typography>
        {sizeLabel && <Typography sx={{ fontSize: 10 }}>{sizeLabel}</Typography>}
      </Box>
    </Box>
  );
}